import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RotateCcw, PenSquare, Check, Clock3, Send } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import AiRfpService from '../../services/aiRfpService';

const buildFallbackContent = () => ({
  scope: {
    title: 'Scope',
    points: [
      'Define project objectives and deliverables.',
      'Specify milestones and acceptance criteria.',
      'Outline expected service levels and quality benchmarks.',
    ],
  },
  technical: {
    title: 'Technical',
    points: [
      'List technical requirements and architecture constraints.',
      'Define integration touchpoints and security controls.',
      'Specify performance and compliance expectations.',
    ],
  },
  eligibility: {
    title: 'Eligibility',
    points: [
      'Vendor qualification and mandatory certifications.',
      'Relevant domain experience and references.',
      'Financial and legal eligibility checks.',
    ],
  },
  commercial: {
    title: 'Commercial',
    points: [
      'Commercial submission format and pricing model.',
      'Payment milestones and contractual assumptions.',
      'Commercial evaluation and award conditions.',
    ],
  },
});

const sectionOrder = ['scope', 'technical', 'eligibility', 'commercial'];

const htmlToPlainText = (value = '') =>
  String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const AiRfpOutputValidation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { error: showError, success, toasts, removeToast } = useToast();

  const incomingGenerated = location.state?.generatedContent;
  const inputPayload = location.state?.inputPayload || null;
  const flowSource = location.state?.source || 'ai-output-validation';
  const isManualTemplateFlow = flowSource === 'template-manual-generate';
  const promptConfigInjection = location.state?.promptConfigInjection || null;
  const draftId = location.state?.draftId || null;

  const [generatedContent] = useState(
    incomingGenerated || buildFallbackContent(),
  );
  const [isRegenerating, setIsRegenerating] = useState(false);

  const sections = useMemo(
    () => sectionOrder.map((key) => generatedContent?.[key]).filter(Boolean),
    [generatedContent],
  );

  const templateVariableMap = useMemo(() => {
    const introPoints = generatedContent?.scope?.points || [];
    const bgPoints = generatedContent?.technical?.points || [];
    const eligibility = generatedContent?.eligibility?.points || [];
    const commercialPoints = generatedContent?.commercial?.points || [];

    return {
      introduction: htmlToPlainText(introPoints.join(' ')),
      project_background: htmlToPlainText(bgPoints.join(' ')),
      eligibility_points: eligibility,
      financial_bid_details: htmlToPlainText(commercialPoints.join(' ')),
      term_sheet: [
        { label: 'Bid Type', value: 'EOI' },
        { label: 'Evaluation Mode', value: 'Two Stage' },
      ],
    };
  }, [generatedContent]);

  const saveStructuredDraft = () => {
    try {
      const draftPayload = {
        savedAt: new Date().toISOString(),
        source: 'ai-output-validation',
        generatedContent,
        inputPayload,
      };
      localStorage.setItem(
        'rfp_ai_generated_draft',
        JSON.stringify(draftPayload),
      );
    } catch (err) {
      // Local save failure should not block navigation.
      // eslint-disable-next-line no-console
      console.warn('Unable to save local AI draft snapshot', err);
    }
  };

  const handleRegenerate = async () => {
    if (isManualTemplateFlow) {
      saveStructuredDraft();
      navigate('/sourcing/rfps/create');
      return;
    }

    try {
      setIsRegenerating(true);
      const response = await AiRfpService.generateDraft({
        draftId,
        inputPayload,
        promptConfig: promptConfigInjection,
      });

      const regeneratedContent = response?.data?.generatedContent;
      const nextDraftId = response?.data?.draftId || draftId;

      if (!regeneratedContent) {
        throw new Error('No regenerated AI content returned.');
      }

      success('AI RFP regenerated successfully.');
      navigate('/sourcing/rfps/create/ai-validation', {
        replace: true,
        state: {
          generatedContent: regeneratedContent,
          inputPayload,
          promptConfigInjection,
          draftId: nextDraftId,
        },
      });
    } catch (err) {
      saveStructuredDraft();
      showError(
        err?.message ||
          err?.response?.data?.message ||
          'Failed to regenerate AI RFP. Local snapshot preserved.',
      );
    } finally {
      setIsRegenerating(false);
    }
  };

  const goToBuilder = (mode) => {
    saveStructuredDraft();
    success('Draft snapshot saved. Opening builder.');
    navigate('/sourcing/rfps/create/manual', {
      state: {
        source: mode,
        generatedContent,
        inputPayload,
        templateVariableMap,
        selectedTemplateContent: location.state?.selectedTemplateContent,
        selectedTemplateId: location.state?.selectedTemplateId,
        selectedTemplateName: location.state?.selectedTemplateName,
        rfpId: draftId,
        sourceRfpId: draftId,
      },
    });
  };

  const goToPublish = () => {
    saveStructuredDraft();
    navigate('/sourcing/rfps/publishing/new', {
      state: {
        generatedContent,
        inputPayload,
        templateVariableMap,
        selectedTemplateName: location.state?.selectedTemplateName,
        aiDraftId: draftId,
      },
    });
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title={isManualTemplateFlow ? 'RFP REVIEW' : 'AI GENERATION RESULT'}
        description={
          isManualTemplateFlow
            ? 'Review generated RFP from selected template before editing or publishing.'
            : 'Review generated structured sections before moving to the builder.'
        }
      />

      <div className="page-container">
        <div
          className="card mb-3"
          style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}
        >
          <div className="card-body" style={{ padding: '18px 20px' }}>
            <div className="d-flex align-items-center gap-2">
              <Clock3 size={16} color="#9a3412" />
              <strong style={{ color: '#9a3412' }}>AI Flow Status</strong>
            </div>
            <p
              className="mb-0 mt-2"
              style={{ color: '#9a3412', fontSize: '13px' }}
            >
              {isManualTemplateFlow
                ? 'This output came from manual template generation. Regenerate returns to AI input while preserving a local snapshot.'
                : 'This screen validates generated sections in UI. Backend re-generation and draft persistence are enabled for AI RFP drafts.'}
            </p>
          </div>
        </div>

        <div className="row g-3">
          {sections.map((section, sectionIndex) => (
            <div className="col-lg-6" key={`${section.title || 'section'}-${sectionIndex}`}>
              <div
                className="card h-100"
                style={{
                  border: '1px solid var(--border-color)',
                  borderRadius: 0,
                }}
              >
                <div className="card-body" style={{ padding: '18px' }}>
                  <h6 className="mb-2">{section.title}</h6>
                  <ul className="mb-0" style={{ paddingLeft: '18px' }}>
                    {(section.points || []).map((point, pointIndex) => (
                      <li
                        key={`${section.title || 'section'}-${pointIndex}-${point}`}
                        style={{ fontSize: '13px', marginBottom: '6px' }}
                      >
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card mt-3" style={{ border: '1px solid var(--border-color)', borderRadius: 0 }}>
          <div className="card-body" style={{ padding: '18px' }}>
            <h6 className="mb-2">AI Output → Template Variable Mapping</h6>
            <div className="table-responsive">
              <table className="table table-sm table-bordered mb-0">
                <thead>
                  <tr>
                    <th>AI Output</th>
                    <th>Template Field</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Introduction</td>
                    <td>{'{{introduction}}'}</td>
                  </tr>
                  <tr>
                    <td>Project Background</td>
                    <td>{'{{project_background}}'}</td>
                  </tr>
                  <tr>
                    <td>Qualifying Requirements</td>
                    <td>{'{{eligibility_points}}'}</td>
                  </tr>
                  <tr>
                    <td>Financial Bid</td>
                    <td>{'{{financial_bid_details}}'}</td>
                  </tr>
                  <tr>
                    <td>Term Sheet (Rows)</td>
                    <td>{'{{term_sheet}}'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <pre
              style={{
                marginTop: '12px',
                marginBottom: 0,
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                padding: '12px',
                fontSize: '12px',
              }}
            >
              {JSON.stringify(templateVariableMap, null, 2)}
            </pre>
            
            {/* Form Actions - Moved inside card and non-sticky */}
            <div
              className="d-flex gap-2 justify-content-end mt-4 pt-4 border-top"
              style={{ flexWrap: 'wrap' }}
            >
              <button
                className="btn btn-outline-secondary"
                onClick={handleRegenerate}
                disabled={isRegenerating}
              >
                <RotateCcw size={16} className="me-1" />
                {isManualTemplateFlow
                  ? 'Back to Inputs'
                  : isRegenerating
                    ? 'Regenerating...'
                    : 'Regenerate'}
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={() => goToBuilder('edit')}
              >
                <PenSquare size={16} className="me-1" />
                Edit in Builder
              </button>
              <button className="btn btn-outline-success" onClick={goToPublish}>
                <Send size={16} className="me-1" />
                Publish
              </button>
              <button
                className="btn btn-primary"
                onClick={() => goToBuilder('accept')}
              >
                <Check size={16} className="me-1" />
                Accept
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AiRfpOutputValidation;
